import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import {
  RiUploadCloud2Line, RiFileExcelLine, RiCloseLine,
  RiCheckLine, RiAlertLine, RiDownloadLine,
} from 'react-icons/ri'

/**
 * Drop-in CSV import button for admin pages.
 *
 *   <CsvImportButton
 *     label="Import services"
 *     uploader={(file) => importAPI.services(file)}
 *     templateUrl="/templates/services.csv"
 *     templateHeaders="category, name, duration, price, description, deposit_amount, is_active"
 *     invalidateKeys={[['admin-services']]}
 *   />
 */
export default function CsvImportButton({
  label,
  uploader,
  templateUrl,
  templateHeaders,
  invalidateKeys = [],
}) {
  const qc = useQueryClient()
  const inputRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [file, setFile] = useState(null)
  const [result, setResult] = useState(null)

  const mut = useMutation({
    mutationFn: (f) => uploader(f),
    onSuccess: (res) => {
      const r = res?.data ?? res
      setResult(r)
      toast.success(r?.message || 'Import complete.')
      invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }))
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || 'Import failed.'
      toast.error(msg)
      setResult({ success: false, message: msg })
    },
  })

  const reset = () => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = '' }
  const close = () => { reset(); setOpen(false) }

  const onPick = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!/\.csv$/i.test(f.name)) { toast.error('Please choose a .csv file.'); return }
    setFile(f); setResult(null)
  }

  const onDrop = (e) => {
    e.preventDefault()
    const f = e.dataTransfer?.files?.[0]
    if (!f) return
    if (!/\.csv$/i.test(f.name)) { toast.error('Please choose a .csv file.'); return }
    setFile(f); setResult(null)
  }

  const onSubmit = () => { if (file) mut.mutate(file) }

  const stats = result?.data || null

  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-secondary text-sm">
        <RiUploadCloud2Line /> Import CSV
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-salon-dark/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={close}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-lg bg-white rounded-3xl shadow-salon-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-salon-primary text-white px-6 py-5 flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-xs uppercase tracking-widest font-body">Bulk import</p>
                  <h2 className="font-display text-xl font-bold">{label}</h2>
                </div>
                <button onClick={close} className="p-2 rounded-lg hover:bg-white/10"><RiCloseLine className="text-xl" /></button>
              </div>

              <div className="p-6 space-y-4">
                {/* Headers hint */}
                {templateHeaders && (
                  <div className="text-xs font-body text-salon-muted bg-salon-cream/40 border border-salon-cream-dark rounded-xl p-3">
                    <p className="font-semibold text-salon-dark mb-1">Required columns</p>
                    <p className="font-mono">{templateHeaders}</p>
                    {templateUrl && (
                      <a href={templateUrl} download className="inline-flex items-center gap-1 text-salon-primary mt-2 hover:underline">
                        <RiDownloadLine /> Download template
                      </a>
                    )}
                  </div>
                )}

                {/* Drop zone */}
                {!result && (
                  <label
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop}
                    className={`block border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors
                                ${file ? 'border-salon-primary/50 bg-salon-primary/5' : 'border-salon-cream-dark hover:border-salon-primary/40 hover:bg-salon-cream/30'}`}
                  >
                    <input
                      ref={inputRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      onChange={onPick}
                    />
                    {file ? (
                      <div>
                        <RiFileExcelLine className="text-4xl text-salon-primary mx-auto mb-2" />
                        <p className="font-display font-semibold text-salon-dark">{file.name}</p>
                        <p className="text-salon-muted font-body text-xs mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); reset() }}
                          className="text-salon-muted hover:text-red-500 text-xs mt-3 font-body"
                        >
                          Choose another file
                        </button>
                      </div>
                    ) : (
                      <div>
                        <RiUploadCloud2Line className="text-4xl text-salon-muted mx-auto mb-2" />
                        <p className="font-display font-semibold text-salon-dark">Drop your CSV here</p>
                        <p className="text-salon-muted font-body text-xs mt-1">or click to browse</p>
                      </div>
                    )}
                  </label>
                )}

                {/* Result */}
                {result && stats && (
                  <div className="rounded-2xl bg-salon-cream/40 border border-salon-cream-dark p-5">
                    <div className="flex items-center gap-2 text-salon-primary font-display font-semibold mb-3">
                      <RiCheckLine /> Import complete
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="font-display text-2xl font-bold text-green-600">{stats.created}</p>
                        <p className="text-xs text-salon-muted font-body">Created</p>
                      </div>
                      <div>
                        <p className="font-display text-2xl font-bold text-blue-600">{stats.updated}</p>
                        <p className="text-xs text-salon-muted font-body">Updated</p>
                      </div>
                      <div>
                        <p className="font-display text-2xl font-bold text-amber-600">{stats.skipped}</p>
                        <p className="text-xs text-salon-muted font-body">Skipped</p>
                      </div>
                    </div>
                    {stats.errors?.length > 0 && (
                      <details className="mt-4 text-xs font-mono">
                        <summary className="cursor-pointer text-amber-700 inline-flex items-center gap-1">
                          <RiAlertLine /> {stats.errors.length} warnings
                        </summary>
                        <ul className="mt-2 space-y-1 text-salon-muted max-h-40 overflow-y-auto pl-4 list-disc">
                          {stats.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  {result ? (
                    <>
                      <button onClick={reset} className="btn-ghost">Import another</button>
                      <button onClick={close} className="btn-primary">Done</button>
                    </>
                  ) : (
                    <>
                      <button onClick={close} className="btn-ghost">Cancel</button>
                      <button
                        onClick={onSubmit}
                        disabled={!file || mut.isPending}
                        className="btn-primary"
                      >
                        {mut.isPending ? 'Uploading…' : 'Upload & Import'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
